import { QueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import { TOAST_CONFIG } from '../hooks/use-toast.hook';
import { AxiosError } from 'axios';
import { addError } from './datadog';
import { getAxiosErrorMessage } from './services';
import { safeRefreshTokenUser } from './services/auth.service';

export enum AppDataQueryKey {
  PublicServices = 'app-data-public-services',
  OrgBasicInfo = 'app-data-org-basic-info',
  AdminUserPermissions = 'app-data-admin-user-permissions',
  Agents = 'app-data-agents',
}

export enum QueryKey {
  AdminUserView = 'admin-user-view',
  AdminUsersDataset = 'admin-users-dataset',
  AgentsList = 'agents-list',
  AgentView = 'agent-view',
  AgentUsage = 'agent-usage',
  AdminLastOwner = 'admin-last-owner',
  AuditLogView = 'audit-log-view',
  AuditLogsDataset = 'audit-logs-dataset',
  AuditLogSettings = 'audit-log-settings',
  AwsConnector = 'aws-connector',
  ClassifierView = 'classifier-view',
  ClassifiersList = 'classifiers-list',
  ClassifiersDataset = 'classifiers-dataset',
  ClassifierUsage = 'classifier-usage',
  ConnectorsDataset = 'connectors-dataset',
  ConnectorMetaView = 'connector-meta-view',
  ConnectorProvidersList = 'connector-providers-list',
  ConnectorSiemsList = 'connector-siems-list',
  ConnectorModelsList = 'connector-models-list',
  ConnectorsTypeList = 'connectors-type-list',
  ConnectorsList = 'connectors-list',
  ContextProvidersDataset = 'context-providers-dataset',
  ContextProvidersList = 'context-providers-list',
  ContextProviderView = 'context-provider-view',
  ContextProviderConnectorDataset = 'context-provider-connectors-dataset',
  ContextProviderUsage = 'context-provider-usage',
  GroupPoliciesDataset = 'group-policies-dataset',
  GroupsDataset = 'groups-dataset',
  GroupsList = 'groups-list',
  GroupsNonSyncOptions = 'groups-non-sync-options',
  GroupView = 'group-view',
  GroupMetaView = 'group-meta-view',
  IdentProviderList = 'ident-provider-list',
  IdentProviderView = 'ident-provider-view',

  // Policy V1
  OrgPolicyView = 'org-policy-view',
  OrgView = 'org-view',
  PolicyView = 'policy-view',
  PolicyCompView = 'policy-comp-view',
  PolicyMetaList = 'policy-meta-list',

  // Policy V2
  ScopedPoliciesDataset = 'scoped-policies-dataset',
  ScopedPolicyWithParents = 'scoped-policy-with-parents',

  PrivateModelViewMeta = 'private-model-view-meta',
  PrivateModelsList = 'private-models-list',
  ProviderModelsList = 'provider-models-list',
  ProxyConnectorDownload = 'proxy-connector-download',
  PublicServicesDataset = 'public-services-dataset',
  PublicServicesDownload = 'public-services-download',
  PublicServiceView = 'public-service-view',
  SupportToken = 'support-token',
  TelemetryDataset = 'telemetry-dataset',
  TelemetryView = 'telemetry-view',
  TelemetrySettings = 'telemetry-settings',
  TelemetryExport = 'telemetry-export',
  UserEventsDataset = 'user-events-dataset',
  UserTimelineDataset = 'user-timeline-dataset',
  UserEventView = 'user-event-view',
  UserEventViewWithFullConversation = 'user-event-view-with-full-conversation',
  UserEventViewWithPartialConversation = 'user-event-with-partial-conversation',
  UserMetaView = 'user-meta-view',
  UsersDataset = 'users-dataset',
  UserSettings = 'user-settings',
  UserUsage = 'user-usage',
  McpServersDataset = 'mcp-servers-dataset',
  McpServerView = 'mcp-server-view',
  McpServersList = 'mcp-servers-list',
  McpToolList = 'mcp-tool-list',
  McpToolView = 'mcp-tool-view',
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 4000, // how long before cached data is marked as stale and automatically refreshed?
      cacheTime: 0, // how long before cached data is removed from cache via gc?
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 2,
      onError: (error: AxiosError) => {
        addError(error);

        const { status } = error || {};

        if (status === 401) {
          safeRefreshTokenUser();
          return;
        }

        const { message } = getAxiosErrorMessage(error, {
          404: 'The resource was not found.',
          429: 'Too many requests. Please wait a minute and try again.',
        });
        enqueueSnackbar(message, { ...TOAST_CONFIG, variant: 'error' });
      },
    },
  },
});

export const refetchQuery = (queryKey: QueryKey | QueryKey[]) => {
  const refetchKeys = Array.isArray(queryKey) ? queryKey : [queryKey];
  refetchKeys.forEach((queryKey) => {
    queryClient.refetchQueries({ queryKey });
  });
};

export default queryClient;
